import { usePostApply } from '@components/hooks/PostApplyProvider/index.client'
import usePostTrialApplyStepViewedEvent from '@components/hooks/usePostTrialApplyStepViewedEvent'
import { CloudflareImage } from '@components/primitives/Image/Cloudflare'
import { Text } from '@components/primitives/Text'
import { appRouterRedirect } from '@lib/routes/helpers/nextjs/appRouterRedirect'
import { PatientQuoteHeadshots } from '@modules/images/components/app/ShareYourStory'
import type { ApplySuccessPageProps } from 'app/p/apply-success/[token]/[step]/page'
import { getApplySuccessRouteToRedirectTo } from './getApplySuccessRouteToRedirectTo'
import { ShareYourStoryPatientQuoteCard } from './ShareYourStoryPatientQuoteCard'

const content = [
  {
    condition: 'Long covid',
    externalId: PatientQuoteHeadshots.patient1,
    quote:
      "I have tried 5 different types of meds and they honestly just made it worse and I'm desperate to find a solution for my issues so i can finally have a happy normal life with my family again.",
  },
  {
    condition: 'Non-Small Cell Lung Cancer',
    externalId: PatientQuoteHeadshots.patient2,
    quote:
      'Presently under Chemotherapy treatment but if something better is out there l would be willing to try it',
  },
  {
    condition: 'Sjogren’s Syndrome',
    externalId: PatientQuoteHeadshots.patient3,
    quote:
      "I've been diagnosed with Sjogren's for over forty years. More and more related autoimmune conditions are accumulating. If there is a chance that something would improve or slow down the progression, I'd be willing to participate in a clinical trial.",
  },
  {
    condition: 'Schizophrenia',
    externalId: PatientQuoteHeadshots.patient4,
    quote:
      "I'm tired of being tired. I don't know who to trust or believe in. I'm scared and hearing voices. People keep calling my name and no one is there.",
  },
  {
    condition: 'Keratoconus',
    externalId: PatientQuoteHeadshots.patient5,
    quote:
      "I am Daniel's mother, and we are desperate for treatment. We don't have medical insurance and treatment is very expensive. I am the only one supporting Daniel. There is no cure and within 2-3 years he has gone from mild to now moderate severe. He is limited on what he can do for employment and has been denied disability. He has to put his cellphone 2 inches from his eyes and cannot see past 5 feet in front of him. He depends on rides to get around. We really need help!",
  },
  {
    condition: 'Non-Alcoholic Fatty Liver Disease',
    externalId: PatientQuoteHeadshots.patient6,
    quote:
      'The info about the trial indicates that it may work for me. I’ve been diagnosed with non alcoholic fatty liver disease. Stenosis hasn’t been mentioned but it’s been a while since I was diagnosed.',
  },
  {
    condition: 'Spinal Cord injury',
    externalId: PatientQuoteHeadshots.patient7,
    quote:
      'I have a spinal cord injury from a herniated disc in my neck that has caused serious stiffness and spasticity in my right leg. I had a cervical fusion over 15 years to correct the problem but the injury persists and my symptoms have worsened over time, impacting my ability to walk.',
  },
  {
    condition: 'Degenerative Disc Disease',
    externalId: PatientQuoteHeadshots.patient8,
    quote:
      'I’m 44 with 2 active kids and I feel like I’m 74. I’ve tried several different medications/injections/therapies and nothing seems to give me long term relief',
  },
]

export function ShareYourStoryPrompt({
  allowPatientCallToSite,
  canPatientContactSite,
  conditionName,
  consentForEmr,
  consentToShareYourStory,
  hasPatientNavigatorAgentCallCompleted,
  idToken,
  isFullyQualified,
  isQualifiedApply,
  mostQualifiedTrialApplicationIdToken,
  showPatientNavigatorAgentStep,
  showScreeningVisitQualificationStep,
  whyAreYouApplying,
}: Pick<
  ApplySuccessPageProps,
  | 'allowPatientCallToSite'
  | 'canPatientContactSite'
  | 'conditionName'
  | 'consentForEmr'
  | 'consentToShareYourStory'
  | 'hasPatientNavigatorAgentCallCompleted'
  | 'idToken'
  | 'isFullyQualified'
  | 'isQualifiedApply'
  | 'mostQualifiedTrialApplicationIdToken'
  | 'showPatientNavigatorAgentStep'
  | 'showScreeningVisitQualificationStep'
  | 'whyAreYouApplying'
>) {
  const { advanceToNextStep } = usePostApply()

  usePostTrialApplyStepViewedEvent({
    mostQualifiedTrialApplicationIdToken,
    trialApplicationIdToken: idToken,
    type: 'share-your-story-step',
  })

  if (!whyAreYouApplying) {
    advanceToNextStep()
    appRouterRedirect(
      getApplySuccessRouteToRedirectTo('share-story', {
        allowPatientCallToSite,
        canPatientContactSite,
        consentForEmr,
        consentToShareYourStory,
        hasPatientNavigatorAgentCallCompleted,
        idToken,
        isFullyQualified,
        isQualifiedApply,
        showPatientNavigatorAgentStep,
        showScreeningVisitQualificationStep,
        whyAreYouApplying,
      }),
    )
    return <></>
  }

  return (
    <div className={'mx-auto grid max-w-4xl grid-cols-1 items-center gap-4'}>
      <Text
        className={`text-xl font-bold md:text-3xl`}
        styleName={'h3'}
        value={'Be a Hero, Share Your Story'}
      />
      <Text
        className='text-neutral600'
        styleName='p-small'
        value={'Can we also share with our community of other patients?'}
      />
      <div className='flex w-full flex-row space-x-4 md:hidden'>
        <ShareYourStoryPatientQuoteCard
          condition={conditionName}
          quote={whyAreYouApplying ?? ''}
        />
      </div>
      <div className='hidden w-full space-x-4 md:flex md:flex-row'>
        <div className='flex w-1/3 flex-col space-y-4'>
          <ShareYourStoryPatientChampionCard {...content[0]!} />
          <ShareYourStoryPatientChampionCard {...content[1]!} />
          <ShareYourStoryPatientChampionCard {...content[2]!} />
        </div>
        <div className='flex w-1/3 flex-col space-y-4'>
          <ShareYourStoryPatientChampionCard {...content[3]!} />
          <ShareYourStoryPatientQuoteCard
            condition={conditionName}
            quote={whyAreYouApplying ?? ''}
          />
          <ShareYourStoryPatientChampionCard {...content[4]!} />
        </div>
        <div className='flex w-1/3 flex-col space-y-4'>
          <ShareYourStoryPatientChampionCard {...content[5]!} />
          <ShareYourStoryPatientChampionCard {...content[6]!} />
          <ShareYourStoryPatientChampionCard {...content[7]!} />
        </div>
      </div>
    </div>
  )
}

function ShareYourStoryPatientChampionCard({
  condition,
  externalId,
  quote,
}: {
  condition: string
  externalId: string
  quote: string
}) {
  return (
    <div className='h-auto w-full rounded-lg border border-neutral100 p-4'>
      <div className='flex flex-col gap-4'>
        <Text
          className={'leading-normal text-neutral500'}
          styleName='p-small'
          value={`"${quote}"`}
        />

        <div className='flex flex-row items-center gap-2'>
          <CloudflareImage
            alt={`Patient with ${condition}`}
            className='rounded-full'
            height={36}
            imageId={externalId}
            width={36}
          />
          <div className='flex flex-col'>
            <Text
              className={'leading-tight text-black'}
              element='p'
              styleName='p-small-strong'
              value='Patient Champion'
            />
            <Text
              className={'leading-tight text-neutral500'}
              element='p'
              styleName='p-small'
              value={condition}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
